import { useEffect, useRef, useState } from 'react';
import { FormInputProps } from '@/@types/form.types';
import EListPopupPickerWithSearch from '@/components/everridge/EListPopupPickerWithSearch';
import { useOwnersLookupApi } from '@/features/owners/hooks/useOwnersLookupApi';

type Props<T extends {}> = {
	selectVariant?: 'single' | 'multi';
	showSearch?: boolean;
	icon?: React.FC<React.SVGProps<SVGSVGElement>>;
} & FormInputProps<T>;

const OwnersLookup = <T extends {}>(props: Props<T>) => {
	const [searchValue, setSearchValue] = useState('');
	const { placeholder = 'Select owner(s)', label = 'Owners' } = props;

	const loaderRef = useRef<HTMLDivElement>(null);

	const {
		ownersLookupList,
		fetchOwnersLookupListNextPage,
		hasOwnersLookupListNextPage,
		isFetchingOwnersLookupListNextPage,
	} = useOwnersLookupApi({ search: searchValue });

	useEffect(() => {
		if (
			!loaderRef.current ||
			!hasOwnersLookupListNextPage ||
			isFetchingOwnersLookupListNextPage
		)
			return;

		const scrollableContainer =
			loaderRef.current.closest('.overflow-y-scroll');

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.isIntersecting) {
					fetchOwnersLookupListNextPage();
				}
			},
			{
				root: scrollableContainer,
				rootMargin: '100px',
			},
		);

		const current = loaderRef.current;
		observer.observe(current);

		return () => observer.unobserve(current);
	}, [
		fetchOwnersLookupListNextPage,
		hasOwnersLookupListNextPage,
		isFetchingOwnersLookupListNextPage,
	]);

	return (
		<EListPopupPickerWithSearch
			{...props}
			label={label}
			placeholder={placeholder}
			list={ownersLookupList ?? []}
			searchValue={searchValue}
			setSearchValue={setSearchValue}
			ref={loaderRef}
		/>
	);
};

export default OwnersLookup;
